export type BenchmarkRange = {
  low: number;
  median: number;
  high: number;
  confidence: "low" | "medium" | "high";
  sourceLabel?: string;
};

export type OutputMetric =
  | "visitors" | "leads" | "qualified_leads" | "booked_calls"
  | "attendees" | "trials" | "customers" | "subscribers" | "revenue";

export type StageTypeMeta = {
  id: string;
  label: string;
  icon: string;
  group: "acquisition" | "nurture" | "conversion" | "retention" | "plg" | "utility";
  defaultCvr: number;
  benchmark: BenchmarkRange;
  outputMetric: OutputMetric;
  subProps?: string[];
  description: string;
};

export const STAGE_TYPE_META: Record<string, StageTypeMeta> = {
  // ACQUISITION
  "blog-post": {
    id: "blog-post", label: "Blog Post / Article", icon: "BP",
    group: "acquisition", defaultCvr: 4,
    benchmark: { low: 2, median: 4, high: 8, confidence: "medium" },
    outputMetric: "leads",
    description: "Organic or promoted article that drives readers into the funnel.",
  },
  "landing-page": {
    id: "landing-page", label: "Landing Page", icon: "LP",
    group: "acquisition", defaultCvr: 28,
    benchmark: { low: 20, median: 28, high: 40, confidence: "high" },
    outputMetric: "leads",
    description: "Dedicated page optimised for a single conversion action.",
  },
  "quiz-page": {
    id: "quiz-page", label: "Quiz / Assessment", icon: "QZ",
    group: "acquisition", defaultCvr: 38,
    benchmark: { low: 30, median: 38, high: 50, confidence: "medium" },
    outputMetric: "leads",
    subProps: ["completionRatePct", "branchCount"],
    description: "Interactive quiz that segments leads and captures email.",
  },
  "advertorial": {
    id: "advertorial", label: "Advertorial / Pre-sell Page", icon: "AV",
    group: "acquisition", defaultCvr: 20,
    benchmark: { low: 15, median: 20, high: 30, confidence: "low" },
    outputMetric: "visitors",
    description: "Editorial-style ad page that warms cold traffic before the offer.",
  },
  "calculator-page": {
    id: "calculator-page", label: "Calculator / ROI Tool", icon: "CA",
    group: "acquisition", defaultCvr: 28,
    benchmark: { low: 20, median: 28, high: 40, confidence: "medium" },
    outputMetric: "leads",
    description: "Interactive calculator that gates results behind an email opt-in.",
  },
  "waitlist-page": {
    id: "waitlist-page", label: "Waitlist / Pre-launch Page", icon: "WL",
    group: "acquisition", defaultCvr: 48,
    benchmark: { low: 40, median: 48, high: 60, confidence: "medium" },
    outputMetric: "leads",
    description: "Captures early interest before a product is publicly available.",
  },

  // NURTURE
  "opt-in-page": {
    id: "opt-in-page", label: "Opt-in / Squeeze Page", icon: "OI",
    group: "nurture", defaultCvr: 38,
    benchmark: { low: 25, median: 38, high: 55, confidence: "high" },
    outputMetric: "leads",
    description: "Single-focus page offering a lead magnet in exchange for email.",
  },
  "email-sequence": {
    id: "email-sequence", label: "Email Sequence", icon: "EM",
    group: "nurture", defaultCvr: 10,
    benchmark: { low: 5, median: 10, high: 20, confidence: "high" },
    outputMetric: "qualified_leads",
    subProps: ["emailCount", "openRatePct", "clickRatePct"],
    description: "Automated series of emails that nurture and qualify subscribers.",
  },
  "vsl-page": {
    id: "vsl-page", label: "Video Sales Letter (VSL)", icon: "VS",
    group: "nurture", defaultCvr: 14,
    benchmark: { low: 8, median: 14, high: 25, confidence: "medium" },
    outputMetric: "leads",
    subProps: ["videoLengthMins", "watchRatePct"],
    description: "Long-form video page designed to educate and pre-sell the offer.",
  },
  "webinar-registration": {
    id: "webinar-registration", label: "Webinar Registration", icon: "WR",
    group: "nurture", defaultCvr: 42,
    benchmark: { low: 30, median: 42, high: 60, confidence: "high" },
    outputMetric: "leads",
    description: "Page collecting registrations for a live or automated webinar.",
  },
  "webinar-live": {
    id: "webinar-live", label: "Webinar Live Room", icon: "WV",
    group: "nurture", defaultCvr: 55,
    benchmark: { low: 40, median: 55, high: 70, confidence: "medium" },
    outputMetric: "attendees",
    subProps: ["attendanceRatePct", "offerTimeMins"],
    description: "Live webinar session where the offer is presented to attendees.",
  },
  "webinar-replay": {
    id: "webinar-replay", label: "Webinar Replay Page", icon: "WP",
    group: "nurture", defaultCvr: 16,
    benchmark: { low: 10, median: 16, high: 25, confidence: "low" },
    outputMetric: "leads",
    subProps: ["replayWatchRatePct"],
    description: "On-demand replay page for leads who missed the live event.",
  },
  "podcast": {
    id: "podcast", label: "Podcast Episode", icon: "PC",
    group: "nurture", defaultCvr: 3,
    benchmark: { low: 1, median: 3, high: 5, confidence: "low" },
    outputMetric: "leads",
    description: "Podcast episode that drives listeners to a CTA or landing page.",
  },
  "challenge-page": {
    id: "challenge-page", label: "Challenge / Boot Camp", icon: "CH",
    group: "nurture", defaultCvr: 25,
    benchmark: { low: 15, median: 25, high: 40, confidence: "medium" },
    outputMetric: "leads",
    subProps: ["durationDays", "completionRatePct"],
    description: "Multi-day challenge that builds engagement and pre-sells the core offer.",
  },

  // CONVERSION
  "sales-page": {
    id: "sales-page", label: "Sales / Long-form Letter", icon: "SL",
    group: "conversion", defaultCvr: 4,
    benchmark: { low: 1, median: 4, high: 8, confidence: "high" },
    outputMetric: "customers",
    description: "Long-form copy page designed to convert warm traffic directly.",
  },
  "order-form": {
    id: "order-form", label: "Order Form / Checkout", icon: "OF",
    group: "conversion", defaultCvr: 65,
    benchmark: { low: 55, median: 65, high: 80, confidence: "high" },
    outputMetric: "customers",
    description: "Payment page where the prospect enters billing details.",
  },
  "free-plus-shipping": {
    id: "free-plus-shipping", label: "Free + Shipping Offer", icon: "FS",
    group: "conversion", defaultCvr: 10,
    benchmark: { low: 5, median: 10, high: 15, confidence: "medium" },
    outputMetric: "customers",
    description: "Low-friction offer that covers shipping cost to acquire a buyer.",
  },
  "tripwire-page": {
    id: "tripwire-page", label: "Tripwire / Low-ticket Offer", icon: "TW",
    group: "conversion", defaultCvr: 13,
    benchmark: { low: 8, median: 13, high: 20, confidence: "medium" },
    outputMetric: "customers",
    description: "Inexpensive entry offer that converts leads into first-time buyers.",
  },
  "pricing-page": {
    id: "pricing-page", label: "Pricing Page (SaaS)", icon: "PP",
    group: "conversion", defaultCvr: 10,
    benchmark: { low: 5, median: 10, high: 15, confidence: "high" },
    outputMetric: "trials",
    subProps: ["planCount"],
    description: "SaaS pricing page comparing plans and driving trial or purchase.",
  },
  "free-trial": {
    id: "free-trial", label: "Free Trial Sign-up", icon: "FT",
    group: "conversion", defaultCvr: 24,
    benchmark: { low: 15, median: 24, high: 35, confidence: "high" },
    outputMetric: "trials",
    subProps: ["trialDays", "requiresCreditCard"],
    description: "Sign-up flow for a time-limited free trial of a SaaS product.",
  },
  "upsell": {
    id: "upsell", label: "Upsell / OTO Page", icon: "UP",
    group: "conversion", defaultCvr: 24,
    benchmark: { low: 15, median: 24, high: 35, confidence: "high" },
    outputMetric: "customers",
    description: "One-time offer shown immediately after an initial purchase.",
  },
  "downsell": {
    id: "downsell", label: "Downsell Page", icon: "DS",
    group: "conversion", defaultCvr: 16,
    benchmark: { low: 10, median: 16, high: 25, confidence: "medium" },
    outputMetric: "customers",
    description: "Alternative lower-priced offer shown after an upsell is declined.",
  },
  "order-bump": {
    id: "order-bump", label: "Order Bump (inline)", icon: "OB",
    group: "conversion", defaultCvr: 28,
    benchmark: { low: 20, median: 28, high: 40, confidence: "medium" },
    outputMetric: "customers",
    description: "Checkbox add-on displayed on the order form before checkout.",
  },
  "product-detail-page": {
    id: "product-detail-page", label: "Product Detail Page (PDP)", icon: "PD",
    group: "conversion", defaultCvr: 13,
    benchmark: { low: 8, median: 13, high: 20, confidence: "medium" },
    outputMetric: "customers",
    description: "E-commerce product page driving add-to-cart actions.",
  },
  "cart-page": {
    id: "cart-page", label: "Cart Page", icon: "CP",
    group: "conversion", defaultCvr: 65,
    benchmark: { low: 55, median: 65, high: 75, confidence: "medium" },
    outputMetric: "customers",
    description: "Shopping cart page that initiates the checkout flow.",
  },
  "application-page": {
    id: "application-page", label: "Application / Survey Page", icon: "AP",
    group: "conversion", defaultCvr: 20,
    benchmark: { low: 10, median: 20, high: 30, confidence: "medium" },
    outputMetric: "qualified_leads",
    description: "Multi-step form qualifying applicants for a high-ticket offer.",
  },
  "booking-page": {
    id: "booking-page", label: "Booking / Calendar Page", icon: "BK",
    group: "conversion", defaultCvr: 44,
    benchmark: { low: 30, median: 44, high: 60, confidence: "high" },
    outputMetric: "booked_calls",
    description: "Calendar booking page where prospects schedule a sales call.",
  },
  "discovery-call": {
    id: "discovery-call", label: "Discovery / Strategy Call", icon: "DC",
    group: "conversion", defaultCvr: 33,
    benchmark: { low: 20, median: 33, high: 50, confidence: "medium" },
    outputMetric: "customers",
    description: "Live sales call converting qualified leads into paying clients.",
  },
  "proposal": {
    id: "proposal", label: "Proposal / Quote Page", icon: "PQ",
    group: "conversion", defaultCvr: 45,
    benchmark: { low: 30, median: 45, high: 60, confidence: "medium" },
    outputMetric: "customers",
    description: "Formal proposal or scoped quote sent after a discovery call.",
  },
  "case-study": {
    id: "case-study", label: "Case Study Page", icon: "CS",
    group: "conversion", defaultCvr: 8,
    benchmark: { low: 5, median: 8, high: 12, confidence: "low" },
    outputMetric: "qualified_leads",
    description: "Detailed client success story that drives qualified demo requests.",
  },

  // RETENTION
  "in-app-upgrade": {
    id: "in-app-upgrade", label: "In-app Upgrade Prompt", icon: "IU",
    group: "retention", defaultCvr: 5,
    benchmark: { low: 2, median: 5, high: 8, confidence: "medium" },
    outputMetric: "customers",
    description: "In-product prompt nudging free users to upgrade to a paid plan.",
  },
  "usage-limit-gate": {
    id: "usage-limit-gate", label: "Usage Limit Paywall", icon: "UG",
    group: "retention", defaultCvr: 10,
    benchmark: { low: 5, median: 10, high: 15, confidence: "medium" },
    outputMetric: "customers",
    description: "Hard gate shown when a user exhausts their free usage quota.",
  },
  "cancellation-save": {
    id: "cancellation-save", label: "Cancellation Save Flow", icon: "CF",
    group: "retention", defaultCvr: 24,
    benchmark: { low: 15, median: 24, high: 35, confidence: "medium" },
    outputMetric: "subscribers",
    description: "Retention flow that rescues subscribers who attempt to cancel.",
  },
  "referral-dashboard": {
    id: "referral-dashboard", label: "Referral / Invite Dashboard", icon: "RD",
    group: "retention", defaultCvr: 12,
    benchmark: { low: 5, median: 12, high: 20, confidence: "low" },
    outputMetric: "visitors",
    description: "In-product referral hub encouraging customers to invite others.",
  },
  "thank-you-page": {
    id: "thank-you-page", label: "Thank You / Delivery Page", icon: "TY",
    group: "retention", defaultCvr: 18,
    benchmark: { low: 10, median: 18, high: 25, confidence: "medium" },
    outputMetric: "customers",
    description: "Post-purchase page delivering the product and presenting an upsell.",
  },

  // PLG
  "activation-event": {
    id: "activation-event", label: "Activation Event (aha moment)", icon: "AE",
    group: "plg", defaultCvr: 50,
    benchmark: { low: 40, median: 50, high: 60, confidence: "medium" },
    outputMetric: "trials",
    description: "Product-defined milestone that marks a user reaching core value.",
  },
  "onboarding-checklist": {
    id: "onboarding-checklist", label: "Onboarding Checklist", icon: "OC",
    group: "plg", defaultCvr: 40,
    benchmark: { low: 30, median: 40, high: 50, confidence: "medium" },
    outputMetric: "trials",
    description: "Guided setup checklist that drives new users to activation.",
  },
  "community-join": {
    id: "community-join", label: "Community Join Page", icon: "CJ",
    group: "plg", defaultCvr: 30,
    benchmark: { low: 20, median: 30, high: 40, confidence: "low" },
    outputMetric: "subscribers",
    description: "Invite page for a paid or gated community product.",
  },

  // UTILITY
  "retainer-close": {
    id: "retainer-close", label: "Retainer / Contract Close", icon: "RC",
    group: "utility", defaultCvr: 60,
    benchmark: { low: 40, median: 60, high: 80, confidence: "medium" },
    outputMetric: "customers",
    description: "Final contract or retainer agreement signed by a B2B client.",
  },
  "affiliate-signup": {
    id: "affiliate-signup", label: "Affiliate / Partner Signup", icon: "AS",
    group: "utility", defaultCvr: 6,
    benchmark: { low: 3, median: 6, high: 10, confidence: "low" },
    outputMetric: "leads",
    description: "Recruitment page for affiliate or JV partners.",
  },
  "share-page": {
    id: "share-page", label: "Share / Viral Page", icon: "SP",
    group: "utility", defaultCvr: 12,
    benchmark: { low: 5, median: 12, high: 20, confidence: "low" },
    outputMetric: "visitors",
    description: "Post-action share prompt encouraging viral referral loops.",
  },
  "retargeting-loop": {
    id: "retargeting-loop", label: "Retargeting Loop", icon: "RT",
    group: "utility", defaultCvr: 18,
    benchmark: { low: 8, median: 18, high: 32, confidence: "medium" },
    outputMetric: "leads",
    subProps: ["sourceAudience", "frequencyCap", "lookbackDays"],
    description: "Paid or owned follow-up loop that brings prior visitors back into the funnel.",
  },
  "wait-delay": {
    id: "wait-delay", label: "Wait / Delay", icon: "WT",
    group: "utility", defaultCvr: 100,
    benchmark: { low: 90, median: 100, high: 100, confidence: "medium" },
    outputMetric: "leads",
    subProps: ["waitDays"],
    description: "Time delay before the next action, used for nurture, sales cycles, and cash timing.",
  },
  "survey-split": {
    id: "survey-split", label: "Survey / Branch Split", icon: "YS",
    group: "utility", defaultCvr: 100,
    benchmark: { low: 85, median: 100, high: 100, confidence: "low" },
    outputMetric: "leads",
    subProps: ["branchCount", "splitPercent"],
    description: "Decision point that routes visitors into branches by answer or eligibility.",
  },
  "custom": {
    id: "custom", label: "Custom Stage", icon: "CU",
    group: "utility", defaultCvr: 20,
    benchmark: { low: 10, median: 20, high: 30, confidence: "low" },
    outputMetric: "leads",
    description: "User-defined stage with manually specified conversion rate.",
  },
};

export function getStageTypeMeta(id: string): StageTypeMeta {
  return STAGE_TYPE_META[id] ?? STAGE_TYPE_META["custom"]!;
}

export function getStageTypesByGroup(): Record<string, StageTypeMeta[]> {
  const groups: Record<string, StageTypeMeta[]> = {};
  for (const meta of Object.values(STAGE_TYPE_META)) {
    if (!groups[meta.group]) groups[meta.group] = [];
    groups[meta.group]!.push(meta);
  }
  return groups;
}

export const ALL_STAGE_TYPE_IDS: string[] = Object.keys(STAGE_TYPE_META);
